import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Inject,
  OnDestroy,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { PoprModalPackage } from './popr-modal.package';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * UI container for modal dialogs.  Wraps a Component instance and mediates opening and closing of the modal.
 */
@Component({
  selector: 'fdx-popr-modal',
  templateUrl: './popr-modal.component.html',
  styleUrls: ['popr-modal.component.css'],
  standalone: false
})
export class PoprModalComponent<T> implements OnDestroy, AfterViewInit {
  private componentSubject$ = new ReplaySubject<T>(1);
  private done = new Subject<void>();
  private onClose = new Subject<void>();

  @ViewChild('dynamicComponent', { read: ViewContainerRef })
  dynamicViewContainerRef!: ViewContainerRef;

  constructor(
    @Inject('dynamicComponentType') private dynamicComponentType: Type<T>,
    private changeDetectorRef: ChangeDetectorRef,
    public poprModalPackage: PoprModalPackage,
    private resolver: ComponentFactoryResolver,
  ) {}

  ngAfterViewInit(): void {
    try {
      this.changeDetectorRef.detach();

      const component = this.createComponent().instance;
      this.componentSubject$.next(component);
      this.componentSubject$.complete();
    } finally {
      this.changeDetectorRef.reattach();
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.done.next();
    this.done.complete();
  }

  /**
   * emit the wrapped component when it is created
   */
  component$(): Observable<T> {
    return this.componentSubject$.asObservable().pipe(takeUntil(this.done));
  }

  /**
   * Listen to close events from this modal
   */
  onClose$(): Observable<void> {
    return this.onClose.asObservable().pipe(takeUntil(this.done));
  }

  /**
   * Close this modal
   */
  close(): void {
    this.onClose.next();
    this.onClose.complete();
  }

  /**
   * @internal
   */
  protected createComponent(): ComponentRef<T> {
    const factory = this.resolver.resolveComponentFactory(this.dynamicComponentType);
    return this.dynamicViewContainerRef.createComponent(factory);
  }
}
